import type { CSSProperties } from "react";
import type { ProgramLayout, ProgramState, SourceSlot } from "./api";

// Multi-head program layouts (ADR 0054; 3/4-up + board-dominant added in ADR 0212).
// MVP multi-speaker is switch-not-composed (ADR 0044): each viewer composites the
// RTC tiles CLIENT-SIDE — there is NO server-side egress compositing. These helpers
// are shared by the Director console (preview/monitors) and the Fan watch so both
// arrange the exact same regions.
//
//  - single      — one source, full frame (today's behavior; the default).
//  - dual        — two heads side by side.
//  - dual-board  — two heads + the shared whiteboard region (ADR 0045 overlay).
//  - dueling     — a 2×2: each head paired with its OWN board (per-slot boards).
//  - tri         — 3-up "Legend large": slot A dominant, B/C stacked beside it.
//  - tri-equal   — three balanced 16:9 heads, 2-over-1 "3-box" (LSC-475).
//  - quad        — 4-up "Legend large": slot A dominant, B/C/D stacked beside it.
//  - quad-equal  — four heads in a 2×2 grid.
//  - board-tri   — board dominant + three small PiP heads (shared board).
//  - board-quad  — board dominant + four small PiP heads (shared board).

/** The Layout selector options (order = single, then heads-only, then board-dominant). */
export const LAYOUT_OPTIONS: { value: ProgramLayout; label: string }[] = [
  { value: "single", label: "Single — one source" },
  { value: "dual", label: "Dual — two heads" },
  { value: "dual-board", label: "Dual + board" },
  { value: "dueling", label: "Dueling — two boards" },
  { value: "tri", label: "3-up — Legend large" },
  { value: "tri-equal", label: "3-up — equal" },
  { value: "quad", label: "4-up — Legend large" },
  { value: "quad-equal", label: "4-up — equal (2×2)" },
  { value: "board-tri", label: "Board + 3-up" },
  { value: "board-quad", label: "Board + 4-up" },
];

/** A layout that renders more than one head (anything but the default single frame). */
export function isMultiHead(layout?: ProgramLayout): boolean {
  return !!layout && layout !== "single";
}

/** A layout that renders any telestration board region — the shared board
 *  (dual-board / board-tri / board-quad) or dueling's two per-slot boards. Used to
 *  decide whether a whiteboard overlay should ALSO compose as a full-frame source
 *  (it shouldn't when the layout already has a dedicated board region). */
export function layoutHasBoard(layout?: ProgramLayout): boolean {
  return layoutHasSharedBoard(layout) || layout === "dueling";
}

/** A layout whose board region is the SINGLE shared whiteboard (ADR 0045 overlay
 *  board+strokes), rendered read-only in the composition's `board` grid area —
 *  dual-board and the board-dominant 3/4-up variants. (dueling's per-slot boards
 *  are separate — `boardA`/`boardB`.) */
export function layoutHasSharedBoard(layout?: ProgramLayout): boolean {
  return layout === "dual-board" || layout === "board-tri" || layout === "board-quad";
}

/** Grid-area names used inside a multi-head composition: `a`/`b`/`c`/`d` are the
 *  heads, `board`/`boardA`/`boardB` the telestration regions. */
export type LayoutArea = "a" | "b" | "c" | "d" | "board" | "boardA" | "boardB";

/** The head slots a layout actually composes, in visual order (slot A is always the
 *  Legend-dominant/first pane). Drives auto-assign, the per-slot Director controls,
 *  the fan tile render, and which sources' audio is played. */
export function layoutSlots(layout?: ProgramLayout): SourceSlot[] {
  switch (layout) {
    case "dual":
    case "dual-board":
    case "dueling":
      return ["a", "b"];
    case "tri":
    case "tri-equal":
    case "board-tri":
      return ["a", "b", "c"];
    case "quad":
    case "quad-equal":
    case "board-quad":
      return ["a", "b", "c", "d"];
    default:
      return ["a"]; // single / undefined
  }
}

/** The on-program STABLE key assigned to a source slot (`a`→source … `d`→sourceD). */
export function slotSource(state: ProgramState, slot: SourceSlot): string | undefined {
  return slot === "a" ? state.source : slot === "b" ? state.sourceB : slot === "c" ? state.sourceC : state.sourceD;
}

/** A ProgramState patch that assigns `key` to a source slot (for staging/assignment). */
export function slotSourcePatch(slot: SourceSlot, key: string): Partial<ProgramState> {
  return slot === "a"
    ? { source: key }
    : slot === "b"
      ? { sourceB: key }
      : slot === "c"
        ? { sourceC: key }
        : { sourceD: key };
}

// ── Shared slot → source resolution (LSC-177) ───────────────────────────────────
// The Fan watch (ConsumerWatch) and the Director confidence monitors (DirectorConsole)
// must composite the SAME source into the SAME slot, always — otherwise the Director's
// PREVIEW/PROGRAM monitors can disagree with what fans receive (and they did, during
// source/layout switching in multi-head layouts — LSC-177). The rules below are the
// SINGLE source of truth both consume, so the two can't diverge by construction.

/** A candidate program source (publisher) as a compositor sees it: its STABLE program
 *  key (the username, ADR 0042) plus whether it currently carries a live video / audio
 *  track. Both the Fan and the Director monitors resolve slots from a list of these. */
export interface SlotCandidate {
  key: string;
  hasVideo?: boolean;
  hasAudio?: boolean;
}

/** Whether a participant is a program SOURCE and not a pure viewer (ADR 0146/LSC-175):
 *  included iff it can publish OR already carries live media. A fan whose LiveKit
 *  `permissions` hasn't loaded yet (`canPublish` unknown) is excluded until it actually
 *  has a track, so a viewer never leaks into the source list as a blank feed (which the
 *  old `permissions && !canPublish` guard let through, dropping the real source). */
export function isProgramSource(p: { canPublish?: boolean; hasVideo?: boolean; hasAudio?: boolean }): boolean {
  return !!(p.canPublish || p.hasVideo || p.hasAudio);
}

/** Choose the best candidate among several sharing ONE stable key — the reconnect case
 *  where a stale ghost (old identity, not yet reaped) and the fresh rejoin both carry
 *  the same key (ADR 0042). Prefer a candidate with a LIVE video track, then one with
 *  live audio, else the first — so the compositor binds the live feed instead of the
 *  dead ghost and never drops the source (ADR 0075/0089/LSC-175). Returns null for an
 *  empty match set. */
export function pickLiveCandidate<T extends { hasVideo?: boolean; hasAudio?: boolean }>(
  matches: readonly T[],
): T | null {
  return matches.find((m) => m.hasVideo) ?? matches.find((m) => m.hasAudio) ?? matches[0] ?? null;
}

/** Resolve, for every slot the layout actually composes (ADR 0054/0212), the STABLE key
 *  assigned to it and the live candidate that serves it — the shared mapping both the
 *  Fan and the Director monitors use so they can't diverge during a source/layout switch
 *  (LSC-177). Slots the layout does not compose are omitted (so collapsing to a smaller
 *  layout drops the extra sources). A slot with no assigned key, or whose key matches no
 *  candidate, resolves `candidate: null` — the caller keeps the current track through a
 *  transient miss while the slot stays assigned (ADR 0062/LSC-425), rather than blanking. */
export function resolveSlotCandidates<T extends SlotCandidate>(
  state: Pick<ProgramState, "layout" | "source" | "sourceB" | "sourceC" | "sourceD">,
  candidates: readonly T[],
): { slot: SourceSlot; key: string | undefined; candidate: T | null }[] {
  return layoutSlots(state.layout).map((slot) => {
    const key = slotSource(state as ProgramState, slot);
    const candidate = key ? pickLiveCandidate(candidates.filter((c) => c.key === key)) : null;
    return { slot, key, candidate };
  });
}

/** Per-slot accent colours (ADR 0108, extended for C/D in ADR 0212). A/B are the
 *  original amber/violet; C/D are teal/rose — all orthogonal to the PREVIEW-blue /
 *  PROGRAM-red bus tally and to each other. The A/B/C/D badge label carries the
 *  identity; the colour is the reinforcing cue. */
export const SLOT_ACCENTS: Record<SourceSlot, string> = {
  a: "#ffd60a", // amber
  b: "#bf5af0", // violet
  c: "#30d0c0", // teal
  d: "#ff6482", // rose
};

/** The CSS grid template (columns / rows / areas) for a multi-head layout — SHARED
 *  by the fan container (layoutContainerStyle) and the Director monitors so the two
 *  never diverge. `single`/unknown falls back to a single full-frame `a` cell. */
export function layoutGridTemplate(layout: ProgramLayout): CSSProperties {
  switch (layout) {
    case "dual":
      return { gridTemplateColumns: "1fr 1fr", gridTemplateAreas: '"a b"' };
    case "dual-board":
      // Two heads on top, the shared board a substantial panel below. Rows are 1fr/2fr
      // (NOT 1fr/1fr): the board spans the FULL width, so an equal-height bottom row made
      // it 16:4.5 = 3.56:1 — a flat strip ("squashed the board to the bottom", LSC-625).
      // Giving the board 2/3 of the stage height makes it 16:6 = 2.67:1 — a real landscape
      // board (consistent with the board-dominant tri/quad boards), heads 8:3 cover-cropped.
      // The band is NOT 16:9; WhiteboardCanvas pillarboxes the board to 16:9 inside it
      // (LSC-648, ADR 0265) so the authored strokes/court never stretch (was 1.5× wide).
      return { gridTemplateColumns: "1fr 1fr", gridTemplateRows: "1fr 2fr", gridTemplateAreas: '"a b" "board board"' };
    case "dueling":
      return { gridTemplateColumns: "1fr 1fr", gridTemplateRows: "1fr 1fr", gridTemplateAreas: '"a boardA" "b boardB"' };
    case "tri":
      // Legend large — slot A dominant (spans both rows), B/C stacked on the side.
      return { gridTemplateColumns: "2fr 1fr", gridTemplateRows: "1fr 1fr", gridTemplateAreas: '"a b" "a c"' };
    case "tri-equal":
      // 2-over-1 "3-box" (LSC-475): two panes on top, one centred below — the
      // broadcast-standard balanced arrangement. A 4-column grid makes every pane a
      // true 16:9 (each = 2/4 width × 1/2 height of a 16:9 stage = 16:9), with side
      // gutters on the centred bottom pane. The naive "a b c" 3-column row produced
      // portrait slivers at ANY size ((16/3)/9 = 0.59), the exact "narrow vertical
      // slices" ADR 0212 said to avoid; this is the documented default (arrangement A).
      return {
        gridTemplateColumns: "1fr 1fr 1fr 1fr",
        gridTemplateRows: "1fr 1fr",
        gridTemplateAreas: '"a a b b" ". c c ."',
      };
    case "quad":
      // Legend large — slot A dominant (spans all three rows), B/C/D stacked.
      return { gridTemplateColumns: "2fr 1fr", gridTemplateRows: "1fr 1fr 1fr", gridTemplateAreas: '"a b" "a c" "a d"' };
    case "quad-equal":
      // Four landscape heads in a 2×2 grid (not four narrow columns).
      return { gridTemplateColumns: "1fr 1fr", gridTemplateRows: "1fr 1fr", gridTemplateAreas: '"a b" "c d"' };
    case "board-tri":
      // Board dominant (top, full width) + three small PiP heads below. The 16:6.75 band
      // is pillarboxed to a 16:9 board by WhiteboardCanvas (LSC-648, ADR 0265).
      return { gridTemplateColumns: "1fr 1fr 1fr", gridTemplateRows: "3fr 1fr", gridTemplateAreas: '"board board board" "a b c"' };
    case "board-quad":
      // Board dominant (top, full width) + four small PiP heads below.
      return {
        gridTemplateColumns: "1fr 1fr 1fr 1fr",
        gridTemplateRows: "3fr 1fr",
        gridTemplateAreas: '"board board board board" "a b c d"',
      };
    default:
      return { gridTemplateColumns: "1fr", gridTemplateAreas: '"a"' };
  }
}

/** The absolutely-positioned container grid for a fan-side multi-head composition. */
export function layoutContainerStyle(layout: ProgramLayout): CSSProperties {
  return {
    position: "absolute",
    inset: 0,
    display: "grid",
    gap: 2,
    background: "var(--surface-2)",
    ...layoutGridTemplate(layout),
  };
}

/** The tile style for a grid area (fills its cell; video covers). */
export function layoutTileStyle(area: LayoutArea): CSSProperties {
  return {
    gridArea: area,
    position: "relative",
    minWidth: 0,
    minHeight: 0,
    overflow: "hidden",
    background: "#000",
  };
}
